import fp from 'lodash/fp';
import { useEffect, useCallback, useState, useMemo } from 'react';
import { Common, API } from '@factorin/domain';

import { useMemoDeps } from 'Modules/ui';
import { useSelector, useAction } from 'Modules/core/redux';

import { paginationActions } from '../state/pagination/actions';
import { IParameters, Pagination } from '../state/pagination/types';
import { paginationStatuses } from '../state/pagination/statuses';
import { getDomainTypeFromPath } from '../state/pagination/utils';
import { Storage } from '../state/storage/types';
import { UsePaginationFiltersReturnType } from './types';


export function usePagination<TElementId = Common.ElementId>(path: string): Pagination<TElementId>;
export function usePagination<TKey extends keyof Pagination, TElementId = Common.ElementId>(
    path: string,
    key: TKey,
): Pagination<TElementId>[TKey];
export function usePagination<TKey extends keyof Pagination>(
    path: string,
    key?: TKey,
): Pagination[TKey] {
    return useSelector(`data.pagination.${path}${key ? `.${key}` : ''}`, [path, key]);
}

export const useStorage = <TItem = any>(domain: string): Storage<TItem> =>
    useSelector(`data.storage.${domain}`, [domain]);

export const useStorageItem = <TItem = any>(domain: string, itemId: Common.ElementId): TItem =>
    useSelector(`data.storage.${domain}.${itemId}`, [domain, itemId]);

export const usePaginationCreate = (path: string, parameters: IParameters = {}) => {
    const hasPagination = useSelector<boolean>(({ data }: any) => !!data.pagination[path], [path]);
    const setParameters = useAction(paginationActions.setParameters);
    useEffect(
        () => {
            if (!hasPagination)
                setParameters(path, parameters);
        },
        [hasPagination, parameters, path, setParameters],
    );
};

export const useRefreshPaginationOnUnmount = (path: string) => {
    const refresh = useAction(
        () => paginationActions.refresh(path, {}),
        [path],
    );
    useEffect(
        () => () => refresh(),
        [refresh],
    );
};

export function usePaginationFilter<TType>(
    path: string,
    field: string,
    mapToParameters?: (value: TType, filters: any) => any,
    partOfField?: string,
) {
    const parameters = usePagination(path, 'parameters') || {};

    const mapFilters = useCallback(
        (value, parameters) => mapToParameters
            ? mapToParameters(value, parameters.filters)
            : {
                filters: {
                    ...parameters.filters,
                    [field]: value,
                },
            },
        [field, mapToParameters],
    );

    const [filter, changeFilterValue] = useState<TType>(
        // @ts-ignore
        () => fp.getOr(
            null,
            partOfField ? `filters[${field}][${partOfField}]` : `filters[${field}]`,
            parameters,
        ),
    );
    const setParameters = useAction(
        (value: TType) => paginationActions.setParametersThrottled(path, {
            ...parameters,
            ...mapFilters(value, parameters),
        }),
        [path, parameters, mapFilters],
    );

    const onChange = useCallback(
        (value: TType) => {
            changeFilterValue(value);
            setParameters(value);
        },
        [setParameters],
    );

    return { value: filter, onChange };
}

export function usePaginationRequests
<TItem extends { Id: number } | { Id: string} | { Uid: string } | { id: string}>(
    path: string,
    parameters: IParameters,
    step: number = 10,
) {
    const [{ start, stop, fetching }, fetch] = useState(() =>
        ({ start: 0, stop: 0, fetching: false }));

    usePaginationCreate(path, parameters);

    const total = usePagination(path, 'total');
    const needFetch = useCallback(
        () => fetch(({ start, stop }) => {
            const newStop = fp.min([total, stop + step]);
            return {
                start: fp.min([newStop, start + step]),
                stop: newStop,
                fetching: true,
            };
        }),
        [total, step],
    );

    const paginationElements = usePagination<
    'elements',
    TItem extends { Id: number } ? number : string
    >(path, 'elements');

    useEffect(
        () => {
            if (!total)
                fetch({ start: 0, stop: 0, fetching: true });

            if (Number.isInteger(total) &&
                total !== 0 &&
                fp.size(paginationElements) === 0) {
                fetch({
                    start: 0,
                    stop: fp.min([total, step]),
                    fetching: true,
                });
            }
        },
        [step, total, paginationElements],
    );

    const request = useAction(
        () => paginationActions.request(
            path,
            { start, stop },
        ),
        [path, start, stop],
    );
    const itemsLoading = useCallback(
        (queue: Pagination['queue']) => queue && queue[`${start}:${stop}`] &&
            queue[`${start}:${stop}`] === paginationStatuses.PENDING,
        [start, stop],
    );
    const itemsLoaded = useCallback(
        (queue: Pagination['queue']) => queue && queue[`${start}:${stop}`] &&
            queue[`${start}:${stop}`] !== paginationStatuses.PENDING,
        [start, stop],
    );
    const queue = usePagination(path, 'queue');
    useEffect(
        () => {
            if (fetching &&
                (!total || fp.size(paginationElements) !== stop) &&
                !itemsLoading(queue) &&
                !itemsLoaded(queue))
                request();
        },
        [
            fetching, stop,
            itemsLoading, itemsLoaded, request,
            total, paginationElements, queue,
        ],
    );

    const refresh = useAction(
        () => paginationActions.refresh(path, {}),
        [path],
    );
    useEffect(() => refresh, [refresh]);

    const [
        elements,
        setElements,
    // eslint-disable-next-line no-extra-parens
    ] = useState<(TItem extends { Id: number } ? number : string)[]>([]);
    const domain = useMemoDeps(getDomainTypeFromPath, [path]);
    const storage = useStorage<TItem>(domain);
    useEffect(
        () => {
            if (itemsLoaded(queue)) {
                const options = fp.filter(element => !!element, paginationElements);
                setElements(options);
                fetch(values => ({ ...values, fetching: false }));
            }
        },
        [queue, paginationElements, storage, itemsLoaded],
    );

    const allLoaded = Number.isInteger(total) &&
        total === fp.size(paginationElements);

    return {
        elements,
        needFetch,
        fetching,
        allLoaded,
        storage,
        total,
    };
}

export function usePaginationFilters<TFilters extends API.FilterQuery>(
    path: string,
): UsePaginationFiltersReturnType<TFilters>;
export function usePaginationFilters<TValue>(
    path: string,
    field: string,
): UsePaginationFiltersReturnType<TValue>
export function usePaginationFilters<TValue>(
    path: string,
    field?: string,
): UsePaginationFiltersReturnType<TValue> {
    const parameters = usePagination(path, 'parameters');

    const setFilters = useAction(
        (value: TValue) =>
            paginationActions.setParametersThrottled(path, {
                ...parameters,
                filters: {
                    ...parameters.filters,
                    ...field ? {
                        [field]: value,
                    } : value,
                },
            }),
        [parameters, field],
    );

    const filters = useMemo(() =>
        field ? fp.getOr('', `filters.${field}`, parameters)
            : fp.getOr({}, 'filters', parameters),
    [parameters, field]);

    return {
        filters,
        setFilters,
    };
}

export * from './types';
